import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { HeroSlide } from '../types';

// Import all hero images
import home0 from '../assets/hero/home0.webp';
import home0Mobile from '../assets/hero/home0-mobile.webp';
import home1 from '../assets/hero/home1.webp';
import home2 from '../assets/hero/home2.webp';
import home3 from '../assets/hero/home3.webp';
import home4 from '../assets/hero/home4.webp';
import home5 from '../assets/hero/home5.webp';
import home6 from '../assets/hero/home6.webp';
import home7 from '../assets/hero/home7.webp';
import home8 from '../assets/hero/home8.webp';
import home9 from '../assets/hero/home9.webp';
import home10 from '../assets/hero/home10.webp';
import home11 from '../assets/hero/home11.webp';
import home12 from '../assets/hero/home12.webp';
import home13 from '../assets/hero/home13.webp';
import home14 from '../assets/hero/home14.webp';
import home15 from '../assets/hero/home15.webp';
import home16 from '../assets/hero/home16.webp';
import home17 from '../assets/hero/home17.webp';
import home18 from '../assets/hero/home18.webp';
import home19 from '../assets/hero/home19.webp';
import home20 from '../assets/hero/home20.webp';

const heroImages = [
  home0,
  home1,
  home2,
  home3,
  home4,
  home5,
  home6,
  home7,
  home8,
  home9,
  home10,
  home11,
  home12,
  home13,
  home14,
  home15,
  home16,
  home17,
  home18,
  home19,
  home20,
];

// Mobile-specific overrides, keyed by slide index
const heroImagesMobile: Record<number, string> = {
  0: home0Mobile,
};

// Feature flag: mobile currently shows only the first slide with no slider controls.
// Flip to true to re-enable the full slider on mobile.
const MOBILE_SLIDER_ENABLED = false;

export default function HeroSection() {
  const { t } = useTranslation('hero');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [, setIsPaused] = useState(false);

  const heroSlides: HeroSlide[] = heroImages.map((img, index) => ({
    id: index + 1,
    imageUrl: img,
    mobileImageUrl: heroImagesMobile[index],
    altText: t(`slides.${index}.alt`),
  }));

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroSlides.length) % heroSlides.length,
    );
  }, [heroSlides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  // useEffect(() => {
  //   if (!isPaused) {
  //     const interval = setInterval(nextSlide, 5000);
  //     return () => clearInterval(interval);
  //   }
  // }, [isPaused, nextSlide]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  return (
    <section
      id='home'
      className='relative h-screen flex items-center justify-center overflow-hidden'
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Image Slider */}
      <div className='absolute inset-0 z-0'>
        <div
          className={`hero-slider relative w-full h-full ${
            MOBILE_SLIDER_ENABLED ? '' : 'mobile-slider-locked'
          }`}
        >
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            >
              <picture>
                {slide.mobileImageUrl && (
                  <source
                    media='(max-width: 767px)'
                    srcSet={slide.mobileImageUrl}
                  />
                )}
                <img
                  src={slide.imageUrl}
                  alt={slide.altText}
                  className='w-full h-full object-cover'
                />
              </picture>
            </div>
          ))}
        </div>
        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className={`slider-control prev absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-16 md:h-16 hexagon bg-yellow-400 hover:bg-yellow-500 items-center justify-center transition-all duration-300 hover:scale-110 ${
            MOBILE_SLIDER_ENABLED ? 'flex' : 'hidden md:flex'
          }`}
          aria-label={t('buttons.previous', { ns: 'common' })}
        >
          <svg
            className='w-6 h-6 md:w-8 md:h-8 text-gray-900'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
              d='M15 19l-7-7 7-7'
            />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className={`slider-control next absolute right-16 md:right-20 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-16 md:h-16 hexagon bg-yellow-400 hover:bg-yellow-500 items-center justify-center transition-all duration-300 hover:scale-110 ${
            MOBILE_SLIDER_ENABLED ? 'flex' : 'hidden md:flex'
          }`}
          aria-label={t('buttons.next', { ns: 'common' })}
        >
          <svg
            className='w-6 h-6 md:w-8 md:h-8 text-gray-900'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
              d='M9 5l7 7-7 7'
            />
          </svg>
        </button>

        {/* Slider Indicators */}
        <div
          className={`absolute bottom-24 left-1/2 -translate-x-1/2 z-20 gap-3 ${
            MOBILE_SLIDER_ENABLED ? 'flex' : 'hidden md:flex'
          }`}
        >
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`slider-indicator w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'active bg-yellow-400'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              data-slide={index}
              aria-label={t('buttons.goToSlide', {
                ns: 'common',
                number: index + 1,
              })}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className='absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 text-center'>
        <div className='text-yellow-400 animate-bounce'>
          <svg
            className='w-6 h-6 mx-auto'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M19 14l-7 7m0 0l-7-7m7 7V3'
            />
          </svg>
        </div>
        <div className='text-white text-sm mt-2'>{t('scrollHint')}</div>
      </div>
    </section>
  );
}
